import { AlertTriangle, Building2, DollarSign, LifeBuoy, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { CustomerSummary } from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { CustomerTable } from "@/components/CustomerTable";
import { EmptyState } from "@/components/EmptyState";
import { InsightCallout } from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { NotesList } from "@/components/NotesList";
import { PageHeader } from "@/components/PageHeader";
import { useCustomers } from "@/hooks/queries";
import { formatInt, formatUsd, formatUsdCompact } from "@/lib/format";

// Customers — the Linear-led Customer-360 list. Customers, status, ARR (Linear revenue proxy) and
// ticket counts come from Linear; device health is joined in for the ~40% that fuzzy-matched a
// warehouse district. owner/tier are not exposed by the Linear API yet, so they read "—".

// Returns null when no customer carries a revenue figure — Linear's bulk API does not expose ARR,
// so summing to "$0" would over-claim real zero revenue. null renders as "— pending" instead.
const sumRevenue = (customers: readonly CustomerSummary[]): number | null => {
  const withRevenue = customers.filter((c) => c.revenueUsd !== null && c.revenueUsd !== undefined);
  if (withRevenue.length === 0) {
    return null;
  }
  return withRevenue.reduce((acc, c) => acc + (c.revenueUsd ?? 0), 0);
};

const sumOpenTickets = (customers: readonly CustomerSummary[]): number =>
  customers.reduce((acc, c) => acc + c.tickets.total, 0);

// "At-risk" = a matched fleet carrying at least one WARN or CRITICAL device. Used to surface the
// highest-ARR account that also has hardware risk — the one worth a call.
const atRiskCount = (c: CustomerSummary): number =>
  c.health ? c.health.riskCounts.warn + c.health.riskCounts.critical : 0;

const topAtRiskByArr = (customers: readonly CustomerSummary[]): CustomerSummary | null => {
  const atRisk = customers.filter((c) => atRiskCount(c) > 0 && c.revenueUsd !== null);
  if (atRisk.length === 0) {
    return null;
  }
  return atRisk.reduce((best, c) => ((c.revenueUsd ?? 0) > (best.revenueUsd ?? 0) ? c : best));
};

export const CustomersPage = (): ReactNode => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useCustomers();
  const meta = data?.meta;
  const payload = data?.data;

  const customers = payload?.customers ?? [];
  const matched = payload?.matchedToDistrict ?? 0;
  const totalArr = sumRevenue(customers);
  const openTickets = sumOpenTickets(customers);
  const topAtRisk = topAtRiskByArr(customers);

  if (isError) {
    return (
      <>
        <PageHeader title="Customers" meta={meta} />
        <ChartCard title="Customers">
          <EmptyState
            title="Couldn't load customers"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Customers"
        description="Linear-led Customer-360 — status, owner, tier and tickets from Linear; device health joined for districts matched by fuzzy name + state. ARR is a Linear revenue proxy."
        meta={meta}
      />

      {topAtRisk ? (
        <InsightCallout
          severity="warn"
          headline={`${topAtRisk.name} is the highest-ARR account carrying hardware risk — ${topAtRisk.revenueUsd !== null ? formatUsd(topAtRisk.revenueUsd) : "—"} ARR with ${formatInt(atRiskCount(topAtRisk))} device(s) at risk.`}
          stats={[
            {
              label: "ARR (proxy)",
              value: topAtRisk.revenueUsd !== null ? formatUsdCompact(topAtRisk.revenueUsd) : "—",
            },
            { label: "Devices at risk", value: formatInt(atRiskCount(topAtRisk)) },
            { label: "Open tickets", value: formatInt(topAtRisk.tickets.total) },
          ]}
          cta={{ label: "Open Customer-360", to: `/customers/${topAtRisk.id}` }}
        />
      ) : null}

      <div className="grid kpi-band">
        <KpiCard
          label="Customers"
          icon={Users}
          loading={isLoading}
          value={customers.length > 0 ? formatInt(customers.length) : "—"}
          source="linear.customers"
        />
        <KpiCard
          label="Matched to a district"
          icon={Building2}
          loading={isLoading}
          value={payload ? formatInt(matched) : "—"}
          source="account_map (fuzzy name+state)"
        />
        <KpiCard
          label="Total ARR (proxy)"
          icon={DollarSign}
          loading={isLoading}
          value={totalArr === null ? "— pending" : formatUsdCompact(totalArr)}
          source="linear.customer.revenue (not exposed by bulk API)"
        />
        <KpiCard
          label="Open tickets"
          icon={LifeBuoy}
          loading={isLoading}
          value={customers.length > 0 ? formatInt(openTickets) : "—"}
          source="linear.issues (customer needs)"
        />
      </div>

      <ChartCard
        title="Customer breakdown"
        subtitle="Search by name; sort by ARR, open tickets, or device at-risk load. Click a row to open the 360."
        source="linear.customers + rpt_hw_device_latest"
        loading={isLoading}
      >
        {customers.length > 0 ? (
          <CustomerTable customers={customers} onRowClick={(c) => navigate(`/customers/${c.id}`)} />
        ) : (
          <EmptyState title="No customers" message="The Customer-360 payload returned no rows." />
        )}
      </ChartCard>

      {meta ? (
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Data notes" loading={isLoading}>
            <NotesList notes={meta.notes} />
          </ChartCard>
        </div>
      ) : null}
    </>
  );
};
